import React from "react";
import { LogoBlackAndWhite } from "assets";
import { TFunction, useTranslation } from "i18n";
import {
  checkIsFilmmakersProfile,
  checkIsGraduate,
  checkIsYp,
} from "shared-auth";
import { UserClaimTypes, UserManager } from "core";
import RepsPrint from "pages/Profile/components/ProfilePrint/components/RepsPrint";
import DetailsPrint from "pages/Profile/components/ProfilePrint/components/DetailsPrint";
import SocialMediaPrint from "pages/Profile/components/ProfilePrint/components/SocialMediaPrint";
import HeadshotPrint from "pages/Profile/components/ProfilePrint/components/HeadshotPrint";
import GalleryPrint from "pages/Profile/components/ProfilePrint/components/GalleryPrint";
import CreditsPrint from "pages/Profile/components/ProfilePrint/components/CreditsPrint";
import SkillsPrint from "pages/Profile/components/ProfilePrint/components/SkillsPrint";
import TrainingPrint from "pages/Profile/components/ProfilePrint/components/TrainingPrint";
import AboutMePrint from "pages/Profile/components/ProfilePrint/components/AboutMePrint";
import { useGetProfileBasicInfoSource } from "pages/Profile/hooks/useGetProfileBasicInfoSource";
import "./styles.scss";

const getPerformerTypeLabel = (
  {
    isFilmmakers,
    isYoungPerformer,
    isGraduate,
  }: {
    isFilmmakers: boolean;
    isYoungPerformer: boolean;
    isGraduate: boolean;
  },
  t: TFunction
) => {
  if (isFilmmakers) return t("common:performer.type.filmmakers");

  if (isYoungPerformer) return t("common:performer.type.youngPerformer");

  if (isGraduate) return t("common:performer.type.graduate");

  return t("common:performer.type.performer");
};

const PerformerProfile = () => {
  const { t } = useTranslation();
  const fullName = UserManager.getClaimWithDefault(
    UserClaimTypes.profileFullName
  );

  const { rawPublicProfile } = useGetProfileBasicInfoSource();
  const artistType = rawPublicProfile?.artistType;
  const isFilmmakers = checkIsFilmmakersProfile(artistType);
  const isYoungPerformer = checkIsYp(artistType);
  const isGraduate = checkIsGraduate(artistType);

  return (
    <div className="profile-print-page hidden min-h-screen justify-center bg-gray-100 py-10 print:flex print:bg-white print:py-0">
      <div className="box-border min-h-[297mm] w-[210mm] bg-white p-[10mm] font-sans text-xs leading-[1.2] text-gray-900 shadow-2xl print:p-[8mm] print:shadow-none">
        <div className="mb-6 flex items-center justify-between border-b border-gray-300 pb-1">
          <div>
            <h1 className="font-secondary text-m font-semibold uppercase leading-tight tracking-tight">
              {fullName}
            </h1>
            <p className="font-secondary text-xs">
              {getPerformerTypeLabel(
                { isFilmmakers, isYoungPerformer, isGraduate },
                t
              )}
            </p>
          </div>
          <LogoBlackAndWhite width={102} height={24} />
        </div>

        <div className="mb-4 grid grid-cols-12 gap-2.5">
          <div className="col-span-3">
            <HeadshotPrint />
            <SocialMediaPrint />
          </div>

          <div className="col-span-9">
            <RepsPrint />
            <div className="divider" />
            <DetailsPrint />
          </div>
        </div>

        <div className="divider" />

        <GalleryPrint />

        <AboutMePrint />

        <div className="divider" />

        <CreditsPrint />

        <div className="divider" />

        <SkillsPrint />

        <TrainingPrint />

        <div className="mt-8 pt-2 text-xs text-gray-400">
          {t("common:nav.footer.copyright")}
          {". "}
          {t("performer:profile.footer.disclaimer")}
        </div>
      </div>
    </div>
  );
};

export default PerformerProfile;
